import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Tag } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader, StatusBadge } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AdminVip() {
  useSEO("Admin · VIP");
  const [settings, setSettings] = useState({ monthly_price: 49, currency: "USD", telegram_vip_link: "", description: "" });
  const [requests, setRequests] = useState([]);
  const [promos, setPromos] = useState([]);
  const [open, setOpen] = useState(false);
  const [promoForm, setPromoForm] = useState({ code: "", benefit_type: "discount", discount_percent: "", duration_days: "", max_uses: "", expiry_date: "", can_combine_with_broker: true, active: true });

  const load = () => {
    api.get("/admin/vip/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/vip/requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/admin/promo-codes").then((r) => setPromos(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try { await api.put("/admin/vip/settings", { ...settings, monthly_price: Number(settings.monthly_price) }); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const activateReq = async (req, status) => {
    try { await api.post(`/admin/vip/${req.user_id}/activate`, { status, source: req.source, lifetime: req.lifetime || false, duration_days: null }); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const createPromo = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/promo-codes", {
        code: promoForm.code, benefit_type: promoForm.benefit_type, active: promoForm.active,
        can_combine_with_broker: promoForm.can_combine_with_broker,
        discount_percent: promoForm.discount_percent ? Number(promoForm.discount_percent) : null,
        duration_days: promoForm.duration_days ? Number(promoForm.duration_days) : null,
        max_uses: promoForm.max_uses ? Number(promoForm.max_uses) : null,
        expiry_date: promoForm.expiry_date || null,
      });
      toast.success("Promo created"); setOpen(false); load();
      setPromoForm({ code: "", benefit_type: "discount", discount_percent: "", duration_days: "", max_uses: "", expiry_date: "", can_combine_with_broker: true, active: true });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delPromo = async (id) => { await api.delete(`/admin/promo-codes/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <AdminHeader title="VIP" subtitle="Manage pricing, activation and codes" />
      <Tabs defaultValue="requests">
        <TabsList className="bg-brand-surface border border-white/10">
          <TabsTrigger value="requests" data-testid="tab-requests">Members / Requests</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Pricing</TabsTrigger>
          <TabsTrigger value="promos" data-testid="tab-promos">Promo Codes</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-4">
          <div className="card-surface rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-white/10"><th className="p-4">User</th><th className="p-4">Source</th><th className="p-4">Quote</th><th className="p-4">Status</th><th className="p-4">Actions</th></tr></thead>
              <tbody>
                {requests.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-slate-500">No VIP requests yet.</td></tr>
                  : requests.map((r) => (
                    <tr key={r.id} className="border-b border-white/5" data-testid={`vip-req-${r.id}`}>
                      <td className="p-4 text-white">{r.user_name}<br /><span className="text-xs text-slate-500">{r.user_email}</span></td>
                      <td className="p-4 text-slate-400 capitalize">{r.source}</td>
                      <td className="p-4 text-slate-400">{r.quote ? (r.quote.final_price === 0 ? "Free" : `$${r.quote.final_price}`) : "—"}</td>
                      <td className="p-4"><StatusBadge status={r.status} /></td>
                      <td className="p-4 flex gap-2">
                        <Button size="sm" className="h-8 bg-brand-green/20 text-brand-green hover:bg-brand-green/30" onClick={() => activateReq(r, "active")} data-testid={`approve-vip-${r.id}`}>Activate</Button>
                        <Button size="sm" variant="outline" className="h-8 border-white/15 text-slate-300" onClick={() => activateReq(r, "inactive")} data-testid={`reject-vip-${r.id}`}>Reject</Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <div className="card-surface rounded-2xl p-7 max-w-lg space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-slate-300">Monthly Price</Label><Input type="number" value={settings.monthly_price} onChange={(e) => setSettings({ ...settings, monthly_price: e.target.value })} data-testid="vip-price-input" className="mt-1.5 bg-white/5 border-white/10" /></div>
              <div><Label className="text-slate-300">Currency</Label><Input value={settings.currency} onChange={(e) => setSettings({ ...settings, currency: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
            </div>
            <div><Label className="text-slate-300">Telegram VIP Link</Label><Input value={settings.telegram_vip_link || ""} onChange={(e) => setSettings({ ...settings, telegram_vip_link: e.target.value })} data-testid="vip-telegram-input" placeholder="https://t.me/..." className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div><Label className="text-slate-300">Description</Label><Input value={settings.description || ""} onChange={(e) => setSettings({ ...settings, description: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
            <Button onClick={saveSettings} className="gradient-brand text-brand-navy font-semibold" data-testid="save-vip-settings">Save Settings</Button>
          </div>
        </TabsContent>

        <TabsContent value="promos" className="mt-4">
          <div className="flex justify-end mb-4">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button className="gradient-brand text-brand-navy font-semibold" data-testid="new-promo-btn"><Plus className="h-4 w-4 mr-1.5" /> New Code</Button></DialogTrigger>
              <DialogContent className="bg-brand-surface border-white/10">
                <DialogHeader><DialogTitle className="text-white">Create Promo Code</DialogTitle></DialogHeader>
                <form onSubmit={createPromo} className="space-y-4">
                  <div><Label className="text-slate-300">Code</Label><Input required value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value })} data-testid="promo-code-input" className="mt-1.5 bg-white/5 border-white/10" /></div>
                  <div>
                    <Label className="text-slate-300">Benefit Type</Label>
                    <Select value={promoForm.benefit_type} onValueChange={(v) => setPromoForm({ ...promoForm, benefit_type: v })}>
                      <SelectTrigger className="mt-1.5 bg-white/5 border-white/10" data-testid="promo-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="discount">Discount %</SelectItem><SelectItem value="free_vip">Free VIP</SelectItem><SelectItem value="free_duration">Free for X days</SelectItem><SelectItem value="lifetime">Lifetime</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-slate-300">Discount %</Label><Input type="number" value={promoForm.discount_percent} onChange={(e) => setPromoForm({ ...promoForm, discount_percent: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
                    <div><Label className="text-slate-300">Duration days</Label><Input type="number" value={promoForm.duration_days} onChange={(e) => setPromoForm({ ...promoForm, duration_days: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
                    <div><Label className="text-slate-300">Max uses</Label><Input type="number" value={promoForm.max_uses} onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
                    <div><Label className="text-slate-300">Expiry</Label><Input type="date" value={promoForm.expiry_date} onChange={(e) => setPromoForm({ ...promoForm, expiry_date: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2.5">
                    <div>
                      <Label className="text-slate-300">Can combine with broker benefit</Label>
                      <p className="text-xs text-slate-500 mt-0.5">If off, this promo won't stack on a broker benefit.</p>
                    </div>
                    <input type="checkbox" checked={promoForm.can_combine_with_broker} onChange={(e) => setPromoForm({ ...promoForm, can_combine_with_broker: e.target.checked })} data-testid="promo-combine-toggle" className="h-5 w-5 accent-brand-cyan" />
                  </div>
                  <DialogFooter><Button type="submit" className="gradient-brand text-brand-navy font-semibold" data-testid="promo-save">Create</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {promos.length === 0 ? <p className="text-slate-500 text-sm col-span-full">No promo codes yet.</p>
              : promos.map((p) => (
                <div key={p.id} className="card-surface rounded-2xl p-5" data-testid={`promo-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-brand-cyan font-semibold flex items-center gap-1.5"><Tag className="h-4 w-4" /> {p.code}</span>
                    <button onClick={() => delPromo(p.id)} className="text-slate-500 hover:text-red-400" data-testid={`del-promo-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <p className="mt-2 text-sm text-slate-300 capitalize">{p.benefit_type.replace("_", " ")}{p.discount_percent ? ` · ${p.discount_percent}%` : ""}{p.duration_days ? ` · ${p.duration_days}d` : ""}</p>
                  <p className="mt-1 text-xs text-slate-500">Used {p.used_count || 0}{p.max_uses ? `/${p.max_uses}` : ""} {p.expiry_date ? `· exp ${new Date(p.expiry_date).toLocaleDateString()}` : ""}</p>
                  <p className="mt-1 text-[11px] text-slate-500">{p.can_combine_with_broker === false ? "✗ Not combinable with broker benefit" : "✓ Combinable with broker benefit"}</p>
                </div>
              ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
